import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';

const TONE = {
  critical: { bg: '#fbe9e7', border: '#f3cfcb', dot: '#b3261e', label: 'Needs you now' },
  warning:  { bg: '#f5f1d6', border: '#7a6a12', dot: '#7a6a12', label: 'Should be dealt with' },
  info:     { bg: '#f1f4f7', border: '#e9eef3', dot: '#8c3242', label: 'Worth knowing' }
};

export default function Notifications() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/api/alerts`)).data); }
    catch (e) { console.error(e); setData({ alerts: [], counts: {}, error: e.message }); }
  }, []);

  useEffect(() => {
    load();
    // The panel is where problems surface, so it must not go stale while
    // it is open and being watched.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const runAction = async (alert, action) => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setBusy(alert.key + action.label);
    try {
      const res = action.method === 'POST'
        ? await axios.post(`${API}${action.url}`, action.body || {})
        : await axios.get(`${API}${action.url}`);
      const d = res.data || {};
      alert1(d.message || 'Done');
      await load();
    } catch (e) {
      alert1('Failed: ' + (e.response?.data?.message || e.message));
    } finally { setBusy(null); }
  };
  const alert1 = (m) => window.alert(m);

  const dismiss = async (a, snoozeHours) => {
    setBusy(a.key + 'dismiss');
    try {
      await axios.post(`${API}/api/alerts/dismiss`, { key: a.key, snoozeHours });
      await load();
    } catch (e) { alert1('Failed: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="card"><p style={{ color: '#93827a' }}>Loading...</p></div>;

  const c = data.counts || {};
  const shown = (data.alerts || []).filter(a => filter === 'all' || a.severity === filter);

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Needs you now</div>
          <div className="stat-value" style={{ color: c.critical ? '#b3261e' : '#2e7d50' }}>{c.critical || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Should be dealt with</div>
          <div className="stat-value" style={{ color: c.warning ? '#7a6a12' : '#93827a' }}>{c.warning || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Worth knowing</div>
          <div className="stat-value" style={{ color: '#8c3242' }}>{c.info || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Money at risk</div>
          <div className="stat-value" style={{ color: data.moneyAtRisk ? '#b3261e' : '#2e7d50', fontSize: '22px' }}>
            ₹{Number(data.moneyAtRisk || 0).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
          <div className="card-title" style={{ margin: 0 }}>
            Notifications {data.dismissed > 0 && <span style={{ color: '#93827a', fontSize: '13px', fontWeight: 400 }}>· {data.dismissed} hidden</span>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[['all', 'All'], ['critical', 'Now'], ['warning', 'Soon'], ['info', 'FYI']].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} style={{
                padding: '7px 14px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer',
                background: filter === v ? '#6e1f2a' : '#ffffff', color: filter === v ? '#fff' : '#7d6a63',
                border: `1px solid ${filter === v ? '#6e1f2a' : '#e6dccc'}`
              }}>{l}</button>
            ))}
            <button onClick={load} style={{ padding: '7px 14px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc' }}>↻</button>
          </div>
        </div>

        {(data.rulesFailing || []).length > 0 && (
          <div style={{ background: '#f5f1d6', border: '1px solid #7a6a12', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: '#7a6a12' }}>
            {data.rulesFailing.length} check(s) could not run: {data.rulesFailing.map(r => r.rule).join(', ')}. The rest of this list is still accurate.
          </div>
        )}

        {shown.length === 0 && (
          <p style={{ color: '#2e7d50', textAlign: 'center', padding: '30px 0' }}>
            Nothing needs you right now 🎉
          </p>
        )}

        {shown.map(a => {
          const t = TONE[a.severity] || TONE.info;
          return (
            <div key={a.key} style={{
              background: t.bg, border: `1px solid ${t.border}`, borderRadius: '10px',
              padding: '14px 16px', marginBottom: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: t.dot, marginTop: '7px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#7d6a63', border: '1px solid #e6dccc', borderRadius: '5px', padding: '1px 7px' }}>{a.category}</span>
                    {a.orderNumber && <span style={{ fontFamily: 'monospace', color: '#8c3242', fontSize: '13px' }}>{a.orderNumber}</span>}
                  </div>
                  <div style={{ fontWeight: 600, color: '#2e1f23', margin: '6px 0 3px' }}>{a.title}</div>
                  <div style={{ fontSize: '13px', color: '#7d6a63' }}>{a.detail}</div>

                  <div style={{ display: 'flex', gap: '8px', marginTop: '11px', flexWrap: 'wrap' }}>
                    {(a.actions || []).map(act => (
                      <button key={act.label} disabled={busy === a.key + act.label}
                        onClick={() => runAction(a, act)}
                        style={{
                          padding: '6px 13px', fontSize: '13px', borderRadius: '7px',
                          cursor: busy === a.key + act.label ? 'wait' : 'pointer',
                          background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc'
                        }}>
                        {busy === a.key + act.label ? 'Working…' : act.label}
                      </button>
                    ))}
                    {a.link && (
                      <button onClick={() => navigate(a.link)} style={{ padding: '6px 13px', fontSize: '13px', borderRadius: '7px', cursor: 'pointer', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc' }}>
                        Open {a.link.replace('/', '')}
                      </button>
                    )}
                    <button onClick={() => dismiss(a, 24)} title="Hide until tomorrow"
                      style={{ padding: '6px 13px', fontSize: '13px', borderRadius: '7px', cursor: 'pointer', background: 'transparent', color: '#93827a', border: '1px solid #e6dccc' }}>
                      Snooze 24h
                    </button>
                    <button onClick={() => dismiss(a, null)} title="Hide for good — it comes back only if the same problem recurs under a new key"
                      style={{ padding: '6px 13px', fontSize: '13px', borderRadius: '7px', cursor: 'pointer', background: 'transparent', color: '#93827a', border: '1px solid #e6dccc' }}>
                      Dismiss
                    </button>
                  </div>
                </div>
                {a.money > 0 && (
                  <div style={{ color: t.dot, fontWeight: 700, fontSize: '15px', whiteSpace: 'nowrap' }}>
                    ₹{Number(a.money).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
